import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  Observable,
  catchError,
  firstValueFrom,
  map,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';

import {
  Configuration,
  ConfigurationKeys,
  Language,
  LanguageResponse,
  SectionType,
  SectionTypeResponse,
} from '@lemonsolutions/models';
import { PrimeNGConfig, TranslationKeys } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  readonly http = inject(HttpClient);
  public config = inject(PrimeNGConfig);
  private languages$!: Observable<Language[]>;
  languages!: Language[];
  private sectionTypes$!: Observable<SectionType[]>;
  sectionTypes!: SectionType[];

  constructor() {
    this.getLanguages().subscribe();
    this.getSectionTypes().subscribe();
    this.getFrontConfigurations().subscribe();
  }

  getLanguage(lang: string): Language | undefined {
    return this.languages?.find(l => l.lang === lang);
  }

  getFrontConfigurations(): Observable<Configuration[]> {
    return this.http
      .get<Configuration[]>(`utils/config`)
      .pipe(
        catchError(err => this.handleError(err, 'Get front configurations'))
      );
  }

  getFrontConfiguration = async (key: ConfigurationKeys) => {
    const value = await firstValueFrom(
      this.getFrontConfigurations().pipe()
    ).then(data => {
      return data.find((c: Configuration) => c.key === key)?.value;
    });
    return value;
  };

  getLanguages(): Observable<Language[]> {
    if (!this.languages$) {
      this.languages$ = this.http.get<LanguageResponse>(`utils/languages`).pipe(
        shareReplay(1),
        tap(resp => (this.languages = resp.data)),
        map(resp => resp.data),
        catchError(err => this.handleError(err, 'Recuperar idiomas'))
      );
    }
    return this.languages$;
  }

  getSectionTypes(): Observable<SectionType[]> {
    if (!this.sectionTypes$) {
      this.sectionTypes$ = this.http
        .get<SectionTypeResponse>(`utils/section_types`)
        .pipe(
          shareReplay(1),
          tap(resp => (this.sectionTypes = resp.data)),
          map(resp => resp.data),
          catchError(err => this.handleError(err, 'Recuperar tipos seccion'))
        );
    }
    return this.sectionTypes$;
  }

  private handleError(err: any, error: string) {
    return throwError(() =>
      err.status === 400 ? err.error.msg : `Se ha producido un error: ${error}`
    );
  }

  async getImageFileFromUrl(image: string, path: string): Promise<Blob | null> {
    return new Promise((resolve, reject) => {
      let blob = null;
      const xhr = new XMLHttpRequest();

      xhr.open('GET', `${path}${image}`, true);
      xhr.responseType = 'arraybuffer';

      xhr.onload = () => {
        blob = new File([xhr.response], `${image}`, { type: 'image/*' });
        if (xhr.status === 200) {
          resolve(blob);
        } else {
          reject(
            new Error(`Failed to fetch image ${image}: ${xhr.statusText}`)
          );
        }
      };
      xhr.onerror = function () {
        reject(new Error('Network error occurred'));
      };
      xhr.send();
    });
  }

  getTranslation(option: string) {
    return this.config.getTranslation(option);
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 3;
    const sizes = this.getTranslation(TranslationKeys.FILE_SIZE_TYPES);

    if (bytes === 0) {
      return `0 ${sizes[0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = (bytes / Math.pow(k, i)).toFixed(dm);

    return `${formattedSize} ${sizes[i]}`;
  }
}
